export const LOGIN_REASONS = ["expired", "logout"] as const;
export type LoginReason = (typeof LOGIN_REASONS)[number];

const PENDING_SSO_DESTINATION_KEY = "pendingSsoDestination";

const AUTH_PATHS = ["/login", "/logout", "/forgot-password", "/reset-password", "/auth", "/consents", "/noCompany"];

const ESTABLISHED_SESSION_KEY = "sessionEstablished";

export const parseLoginReason = (raw: string | null | undefined): LoginReason | null => {
    return LOGIN_REASONS.find((reason) => reason === raw?.trim()) ?? null;
};

export const markSessionEstablished = (): void => {
    try {
        window.localStorage.setItem(ESTABLISHED_SESSION_KEY, "1");
    } catch {
        return;
    }
};

export const hasEstablishedSession = (): boolean => {
    try {
        return window.localStorage.getItem(ESTABLISHED_SESSION_KEY) !== null;
    } catch {
        return false;
    }
};

export const forgetEstablishedSession = (): void => {
    try {
        window.localStorage.removeItem(ESTABLISHED_SESSION_KEY);
    } catch {
        return;
    }
};

export const resolveEntryReason = (requested: LoginReason | null, hasSession: boolean): LoginReason | undefined => {
    if (requested === null) return "logout";
    if (requested === "expired" && hasSession === false) return undefined;

    return requested;
};

export const isSafeDestination = (raw: string | null | undefined): boolean => {
    if (raw === null || raw === undefined) return false;

    const value = raw.trim();

    if (!value.startsWith("/")) return false;

    try {
        const origin = window.location.origin;

        return new URL(value, origin).origin === origin;
    } catch {
        return false;
    }
};

type Location = { pathname: string; search?: string; hash?: string };

export const captureDestination = (location: Location): string | null => {
    const isAuthPath = AUTH_PATHS.some((path) => location.pathname.startsWith(path));
    if (isAuthPath) return null;

    const destination = `${location.pathname}${location.search ?? ""}${location.hash ?? ""}`;

    return isSafeDestination(destination) && destination !== "/" ? destination : null;
};

type EntryParams = {
    reason?: LoginReason;
    destination?: string | null;
};

const buildEntryUrl = (path: string, params: EntryParams): string => {
    const search = new URLSearchParams();

    if (params.reason !== undefined) search.set("reason", params.reason);
    if (isSafeDestination(params.destination)) search.set("destination", (params.destination ?? "").trim());

    const query = search.toString();

    return query === "" ? path : `${path}?${query}`;
};

export const buildLogoutUrl = (params: EntryParams = {}): string => buildEntryUrl("/logout", params);
export const buildLoginUrl = (params: EntryParams = {}): string => buildEntryUrl("/login", params);
export const buildForgotPasswordUrl = (params: EntryParams = {}): string => buildEntryUrl("/forgot-password", params);

export const storePendingSsoDestination = (destination: string | null | undefined): void => {
    try {
        if (isSafeDestination(destination)) {
            window.sessionStorage.setItem(PENDING_SSO_DESTINATION_KEY, (destination ?? "").trim());
            return;
        }
        window.sessionStorage.removeItem(PENDING_SSO_DESTINATION_KEY);
    } catch {
        return;
    }
};

export const forgetPendingSsoDestination = (): void => storePendingSsoDestination(null);

export const consumePendingSsoDestination = (): string | null => {
    try {
        const destination = window.sessionStorage.getItem(PENDING_SSO_DESTINATION_KEY);
        window.sessionStorage.removeItem(PENDING_SSO_DESTINATION_KEY);

        return isSafeDestination(destination) ? destination : null;
    } catch {
        return null;
    }
};
